/**
 * Arriving at a request WITH its discussion showing (#1069; owner change
 * 2026-09-11, routed to quick-dev after Story 2.2).
 *
 * The request-list marker (`DiscussionRowIndicator`) opens the request with
 * the panel already open; every other way into the request arrives with the
 * panel closed (Story 5.3). The two screens agree through this file: the list
 * builds the path here, and `RequestDetail` reads the flag here, so the
 * parameter name and its one accepted value are written exactly once.
 *
 * ⚠️ A QUERY PARAMETER, NOT SCREEN STATE, ON PURPOSE. `App.tsx` carries the
 * whole query string through `navigate` on web (`history.pushState`) and on
 * native (`__currentRoute`), and `RequestDetail.getQueryParam` reads either;
 * a piece of screen state would need a second contract per platform. The
 * flag is read ONCE, when the screen mounts (`useState` initialiser), so a
 * later close is not undone by a re-render, and it is never written back.
 *
 * `thread=` (#1599 Story 2.3, FR-8) names one conversation to land on. It is
 * honoured ONLY beside `discussion=open`: a `thread=` alone leaves the panel
 * shut, because opening the panel from a second parameter would be a second
 * way in, which Story 5.3 removed. And the id is never opened directly. The
 * panel RESOLVES it against the conversation listings the viewer may read
 * (`useArrivalThread`): opening an unreadable id would render the messages
 * query's error bar, and opening a readable one without its space would show
 * a shared conversation under the Vertiv tab, where every reply goes to the
 * customer. A row found in a listing opens in its own space; anything else
 * leaves the list showing, with no error.
 */

export const DISCUSSION_QUERY_PARAM = 'discussion';

/** The one value that opens the panel on arrival. Anything else is ignored. */
export const DISCUSSION_QUERY_OPEN = 'open';

/**
 * The request-detail path that arrives with the discussion open. `id` is
 * encoded, like every other id the list puts in a path.
 */
export const requestDetailPathWithDiscussion = (requestId: string): string =>
  `/request-detail?id=${encodeURIComponent(
    requestId,
  )}&${DISCUSSION_QUERY_PARAM}=${DISCUSSION_QUERY_OPEN}`;

/**
 * Does this query value ask for the discussion open on arrival? Exact match
 * only: `'1'`, `'true'`, `'OPEN'` do not, so a stray value cannot open it.
 */
export const arrivesWithDiscussionOpen = (
  value: string | null | undefined,
): boolean => value === DISCUSSION_QUERY_OPEN;

/** The parameter that names the conversation to land on (#1599 Story 2.3). */
export const THREAD_QUERY_PARAM = 'thread';

/**
 * The request-detail path that arrives with the discussion open ON one
 * conversation. The same shape as the server's `openConversationUrl`
 * (`digest-render.ts`), so the partition guard can compare the two.
 */
export const requestDetailPathWithConversation = (
  requestId: string,
  threadId: string,
): string =>
  `${requestDetailPathWithDiscussion(
    requestId,
  )}&${THREAD_QUERY_PARAM}=${encodeURIComponent(threadId)}`;

/** A positive decimal integer with no leading zero: what a thread id is. */
const THREAD_ID_SHAPE = /^[1-9]\d{0,9}$/;
/** SQL Server's `INT` ceiling: a larger value cannot name a row. */
const THREAD_ID_MAX = 2147483647;

/**
 * The conversation id an arrival names, or `null`. The server's thread ids
 * are positive `INT`s and the client's `Thread.id` is their decimal string,
 * so a value that cannot name a row is `null` here, at the door, and never
 * reaches the session or a request.
 */
export const arrivalThreadIdFrom = (
  value: string | null | undefined,
): string | null =>
  typeof value === 'string' &&
  THREAD_ID_SHAPE.test(value) &&
  Number(value) <= THREAD_ID_MAX
    ? value
    : null;
